import React, { useEffect, useState } from 'react';
import SimpleDialog, { SimpleDialogProps } from '../SimpleDialog/SimpleDialog';
import UploadFilename from '../../UploadFilename/UploadFilename';
import DateTimeWithOffset from '../../DateTimeWithOffset/DateTimeWithOffset';
import { useTranslation } from 'react-i18next';
import UTCTooltip from '../../UTCTooltip/UTCTooltip';
import { getUserOffset } from '../../../helpers/timezone';
import { AppState } from '../../../store/state';
import { selectCurrentUser } from '../../../store/selectors';
import { connect } from 'react-redux';
import { APIValidationError } from '../../../store/api';
import { Result } from '../../../store/api.d';
import Loading from '../../Loading/Loading';

interface Props extends SimpleDialogProps {
  timezone: string | null;
  onShowHistory: () => Promise<Result<GetFileUploadsResponse, APIValidationError>>;
}

const FileUploadHistoryDialog: React.FC<Props> = ({ timezone, onShowHistory, ...rest }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);

  async function fetchData() {
    const result = await onShowHistory();
    setIsLoading(false);
    switch (result.type) {
      case 'ok':
        setFileUploads(result.value.data);
        break;
      case 'validation-error':
        break;
      case 'error':
        break;
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const title = (
    <div className="flex justify-between items-center">
      <span>{t('File upload history')}</span>
      <UTCTooltip utcOffset={getUserOffset(timezone)} />
    </div>
  );

  const rows: JSX.Element[] = [];
  fileUploads.forEach((fileUpload) => {
    rows.push(
      <tr key={rows.length} className="hover:bg-gray-50">
        <td className="px-6 py-4">
          <UploadFilename path={fileUpload.path} filename_original={fileUpload.filename_original} />
        </td>
        <td className="px-6 py-4 font-mono text-gray-600">{fileUpload.md5}</td>
        {fileUpload.created_at && (
          <td className="px-6 py-4 text-gray-500">
            <DateTimeWithOffset date={fileUpload.created_at} showTime />
          </td>
        )}
      </tr>,
    );
    if (fileUpload.deleted_at) {
      rows.push(
        <tr key={rows.length} className="hover:bg-gray-50">
          <td className="px-6 py-4">
            <span className="text-black">--- {t('File Deleted')} ---</span>
          </td>
          <td className="px-6 py-4 font-mono text-gray-600"></td>
          <td className="px-6 py-4 text-gray-500">
            <DateTimeWithOffset date={fileUpload.deleted_at} showTime />
          </td>
        </tr>,
      );
    }
  });

  return (
    <SimpleDialog {...rest} title={title}>
      {isLoading ? (
        <Loading compact />
      ) : fileUploads.length > 0 ? (
        <div className="max-w-5xl mx-auto bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto text-sm text-left text-gray-700">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-6 py-3">Filename</th>
                  <th className="px-6 py-3">MD5 Checksum</th>
                  <th className="px-6 py-3">Datetime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">{rows}</tbody>
            </table>
          </div>
        </div>
      ) : (
        <div>{t('No records to display')}</div>
      )}
    </SimpleDialog>
  );
};

const mapStateToProps = (state: AppState) => ({
  timezone: selectCurrentUser(state).person.timezone,
});

export default connect(mapStateToProps)(FileUploadHistoryDialog);
